/**
 * होम — the app's first screen after login (N-PAC, 4 Nov 2026: "nine option है, nine icon लगाएगा … दस आइकन
 * जोड़ना होगा"): one big icon per section of the work, in N-PAC's order — बूथ, सर्वे, प्रत्याशी भ्रमण,
 * रैली / नुक्कड़ / सभा, प्रचार गाड़ी, मतदान दिवस, समीक्षा, एग्ज़िट पोल, सिंक, कार्यकर्ता ID स्कैन (and the
 * duties given earlier: सभी जगहें, वोट डाला, मतगणना). Only the sections this login's duties open are shown
 * ("जो लॉगिन आइडी पासवर्ड डालेगा उसका ऑप्शन खुल जायेगा"); the admin panel decides those.
 */
import { moduleLocked, type ModuleKey } from "@npac/rules";
import { useEffect, useState } from "react";
import { useApp, useEmitter, useSession, useSyncStatus } from "../ui/context";
import { href, type Route } from "../ui/router";

export type HomeTile = {
  key: string;
  icon: string;
  route: Route;
  title: string;
  sub: string;
  tone: string;
  /** the web module this section belongs to — locked there (N-PAC's subscription) = locked here */
  module?: ModuleKey;
};

/** the tiles a login gets, in N-PAC's order (the role-wide ones first, then the duty-wise ones) */
export function homeTiles(screens: string[], role: string | undefined, t: ReturnType<typeof useApp>["t"]): HomeTile[] {
  const has = (s: string) => screens.includes(s);
  const admin = role === "super_admin" || role === "assembly_admin" || role === "viewer";
  const boothWork = screens.some((x) => !["places", "rallies", "tour", "vehicles", "exit_poll", "voted", "counting", "survey", "attendance"].includes(x));
  const all: (HomeTile & { on: boolean })[] = [
    { key: "booths", icon: "🏫", route: { name: "booths" }, title: t.tabBooths, sub: t.hmBooths, tone: "#e3f0ff", module: "booths", on: boothWork },
    { key: "survey", icon: "📋", route: { name: "survey" }, title: t.tabSurvey, sub: t.hmSurvey, tone: "#e9f8e6", module: "survey", on: has("survey") },
    { key: "tour", icon: "🚩", route: { name: "camp", part: "tour" }, title: t.camp_tour, sub: t.campSub_tour, tone: "#ffe6ee", module: "tour", on: has("tour") },
    { key: "rallies", icon: "📣", route: { name: "camp", part: "rallies" }, title: t.camp_rallies, sub: t.campSub_rallies, tone: "#fff1db", module: "rallies", on: has("rallies") },
    { key: "vehicles", icon: "🛺", route: { name: "camp", part: "vehicles" }, title: t.camp_vehicles, sub: t.hmVehicles, tone: "#dff7fb", module: "vehicles", on: has("vehicles") },
    { key: "polling", icon: "🚨", route: { name: "polling" }, title: t.tabPolling, sub: t.hmPolling, tone: "#ffe3e3", module: "pollday", on: role !== "viewer" },
    { key: "review", icon: "👁", route: { name: "review" }, title: t.rvTitle, sub: t.hmReview, tone: "#efe9ff", on: admin },
    { key: "exitpoll", icon: "📊", route: { name: "exitpoll" }, title: t.tabExit, sub: t.hmExit, tone: "#e6f4ff", module: "exitpoll", on: has("exit_poll") },
    { key: "sync", icon: "✓", route: { name: "sync" }, title: t.tabSync, sub: t.hmSync, tone: "#eef1f5", on: true },
    { key: "attendance", icon: "🪪", route: { name: "attendance" }, title: t.hmAttendance, sub: t.hmAttendanceSub, tone: "#fff8d6", module: "attendance", on: has("attendance") },
    { key: "allplaces", icon: "📍", route: { name: "allplaces" }, title: t.tabPlaces, sub: t.hmPlaces, tone: "#e9f8e6", module: "places", on: has("places") },
    { key: "voted", icon: "🗳", route: { name: "voted" }, title: t.tabVoted, sub: t.hmVoted, tone: "#e3f0ff", module: "voted", on: has("voted") },
    { key: "counting", icon: "🧮", route: { name: "counting" }, title: t.tabCounting, sub: t.hmCounting, tone: "#fff1db", module: "counting", on: has("counting") },
  ];
  return all.filter((x) => x.on).map(({ on: _on, ...tile }) => tile);
}

export function HomeScreen() {
  const { app, t } = useApp();
  const { user } = useSession();
  const status = useSyncStatus();
  const tick = useEmitter(app.core.changes);
  const tiles = homeTiles(app.screens, user?.role, t);
  const bad = status.counts.failed + status.counts.conflict > 0;
  // module locks (N-PAC's subscription, 4 Nov 2026): a locked section stays on the screen, greyed with a lock
  const [mods, setMods] = useState<string[] | null>(null);
  const [lockNote, setLockNote] = useState<string | null>(null);
  useEffect(() => {
    let off = false;
    void app.lockedModules().then((m) => {
      if (!off) setMods(m);
    });
    return () => {
      off = true;
    };
  }, [app, tick, app.assemblyId]);
  const isLocked = (x: HomeTile) => !!x.module && moduleLocked(mods, x.module);
  return (
    <div className="home" data-testid="home">
      <p className="home-hello">
        {t.hmHello(user?.name ?? "")} <span className="muted">{t.hmCount(tiles.length)}</span>
      </p>
      <div className="home-grid">
        {tiles.map((x, i) => {
          const inner = (
            <>
              <span className="home-no" aria-hidden>
                {i + 1}
              </span>
              <span className="home-ic" aria-hidden>
                {x.key === "sync" ? (status.running ? "⟳" : status.counts.total ? "⇅" : "✓") : x.icon}
                {x.key === "sync" && status.counts.total ? <span className={`badge${bad ? " bad" : ""}`}>{status.counts.total}</span> : null}
              </span>
              <b>{x.title}</b>
              <small>{isLocked(x) ? `🔒 ${t.hmLocked}` : x.sub}</small>
            </>
          );
          const style = { ["--tone" as string]: x.tone } as React.CSSProperties;
          return isLocked(x) ? (
            <button type="button" key={x.key} className="home-tile home-locked" style={style} data-testid={`home-${x.key}`} data-locked="1" onClick={() => setLockNote(x.title)}>
              {inner}
            </button>
          ) : (
            <a key={x.key} href={href(x.route)} className={`home-tile${x.key === "polling" ? " home-sos" : ""}`} style={style} data-testid={`home-${x.key}`}>
              {inner}
            </a>
          );
        })}
      </div>
      {lockNote ? (
        <div className="banner banner-warn home-lock-note" role="status" data-testid="home-lock-note">
          <b>🔒 {lockNote}</b> — {t.hmLockedNote}
          <button type="button" className="btn-link" onClick={() => setLockNote(null)}>
            {t.hmLockedOk}
          </button>
        </div>
      ) : null}
      <p className="muted home-note">{t.hmNote}</p>
    </div>
  );
}
